// Sonic Calendar tab: first-run setup (calendar, hours, appointment length, booking link: one question each), then Appointments,
// Settings and More options.
// One job per screen: small businesses use this, so advanced options stay behind More options.
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Spin, message } from "antd";
import { disconnectCalendar, getCalendar, listCalendars, saveCalendar, saveConnection, startOAuth } from "./calendarService";
import { hoursSummary } from "./calendarShared";
import MoreOptions from "./MoreOptions";
import dayjs from "dayjs";
import {
  AddAppointmentScreen,
  AppointmentsScreen,
  BlockTimeScreen,
  CalendarScreen,
  DaysOffScreen,
  HoursScreen,
  LengthScreen,
  LinkScreen,
  PROVIDERS,
  SettingsScreen,
  StaffScreen,
} from "./Screens";
import spStyles from "../SonicProspect/style.module.scss";

// Setup answers not yet saved survive a reload or a visit elsewhere (this browser only).
const draftKey = (botId) => `sonic-calendar-setup:${botId}`;
const readDraft = (botId) => {
  try {
    return JSON.parse(window.localStorage.getItem(draftKey(botId)) || "null");
  } catch (_) {
    return null;
  }
};
const writeDraft = (botId, draft) => {
  try {
    if (draft) window.localStorage.setItem(draftKey(botId), JSON.stringify(draft));
    else window.localStorage.removeItem(draftKey(botId));
  } catch (_) {
    // storage blocked: setup just starts over next time
  }
};

const browserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch (_) {
    return null;
  }
};

/** "29 Oct, 1 Jan +2" for the next whole days off; "None" when there are none. */
const daysOffSummary = (overrides, t) => {
  const today = dayjs().format("YYYY-MM-DD");
  const next = overrides.filter((o) => o.closed && o.date >= today).map((o) => dayjs(o.date).format("D MMM"));
  if (!next.length) return t("sonic_calendar.none");
  return next.length > 3 ? `${next.slice(0, 3).join(", ")} +${next.length - 3}` : next.join(", ");
};

const CalendarTab = ({ bot }) => {
  const { t, i18n } = useTranslation();
  const botId = bot?.id;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [slug, setSlug] = useState("");
  const [meta, setMeta] = useState({});
  const [connection, setConnection] = useState(null);
  const [calendars, setCalendars] = useState([]);
  const [dirty, setDirty] = useState(false);
  // setup1..setup3 | appointments | settings | hours | services | calendar | more
  const [view, setView] = useState(null);
  // Staff member whose own hours are being edited (view "staffhours").
  const [hoursFor, setHoursFor] = useState(null);

  const load = useCallback(async () => {
    if (!botId) return;
    setLoading(true);
    try {
      const res = await getCalendar(botId);
      let config = res.config;
      const draft = res.set_up ? null : readDraft(botId);
      if (!res.set_up) {
        const tz = browserTimezone();
        if (tz) config.availability = { ...config.availability, timezone: tz };
        if (draft?.cfg) config = { ...config, ...draft.cfg };
      }
      setEnabled(!!res.enabled);
      setCfg(config);
      setSlug(res.booking_slug || "");
      setConnection(res.connection);
      setMeta({
        pageUrl: res.booking_page_url,
        providers: res.providers || {},
        pricingUrl: res.pricing_url,
        knownCustomers: res.known_customers || 0,
        calendlyServices: res.calendly_services || null,
        linkBase: res.booking_link_base,
        suggestedSlug: res.suggested_slug || "",
        limitReached: !!res.new_customer_limit_reached,
      });
      setDirty(false);
      // Back from Google or Microsoft mid-setup: carry on with the hours.
      const connected = res.connection && res.connection.provider !== "calendly";
      const resumeAt = draft?.view && draft.view !== "setup1" ? draft.view : connected ? "setup2" : "setup1";
      setView((v) => v || (res.set_up ? "appointments" : resumeAt));
    } catch (err) {
      message.error(t("sonic_calendar.load_error"));
    } finally {
      setLoading(false);
    }
  }, [botId, t]);

  useEffect(() => {
    load();
    // Back from Google or Microsoft: setup continues with the hours, or lands on Appointments when done.
    const connected = () => {
      setView(null);
      load();
    };
    window.addEventListener("sonic-calendar-connected", connected);
    return () => window.removeEventListener("sonic-calendar-connected", connected);
  }, [load]);

  useEffect(() => {
    if (!botId || !connection || connection.status !== "connected" || connection.provider === "calendly") {
      setCalendars([]);
      return;
    }
    listCalendars(botId)
      .then((res) => setCalendars(res.calendars || []))
      .catch(() => setCalendars([]));
  }, [botId, connection]);

  /** Saves the whole calendar; true when it worked. */
  const persist = async (nextCfg = cfg, nextEnabled = enabled, nextSlug = slug) => {
    setSaving(true);
    try {
      const res = await saveCalendar(botId, { enabled: nextEnabled, config: nextCfg, bookingSlug: nextSlug });
      setCfg(res.config);
      setEnabled(nextEnabled);
      setSlug(res.booking_slug || "");
      setMeta((m) => ({ ...m, pageUrl: res.booking_page_url }));
      setDirty(false);
      writeDraft(botId, null);
      return true;
    } catch (err) {
      message.error(err.code === "slug_taken" ? t("sonic_calendar.slug_taken") : t("sonic_calendar.save_error"));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveAndGo = async (nextCfg, nextView) => {
    if (await persist(nextCfg)) {
      message.success(t("sonic_calendar.saved"));
      setView(nextView);
    }
  };

  const handleConnect = async (provider) => {
    try {
      const res = await startOAuth(botId, provider);
      if (!res.configured || !res.authorize_url) {
        message.warning(t("sonic_calendar.provider_not_configured"));
        return;
      }
      window.location.assign(res.authorize_url);
    } catch (_) {
      message.error(t("sonic_calendar.connect_error"));
    }
  };

  const handleDisconnect = async () => {
    await disconnectCalendar(botId).catch(() => null);
    setConnection(null);
    message.success(t("sonic_calendar.disconnected"));
    load();
  };

  const handleCalendarPick = async (patch) => {
    const next = {
      busyCalendarIds: patch.busy ?? connection.busy_calendar_ids,
      writeCalendarId: patch.write ?? connection.write_calendar_id,
    };
    try {
      const res = await saveConnection(botId, next);
      setConnection(res.connection);
    } catch (_) {
      message.error(t("sonic_calendar.save_error"));
    }
  };

  useEffect(() => {
    if (botId && cfg && view && view.startsWith("setup")) writeDraft(botId, { view, cfg });
  }, [botId, cfg, view]);

  if (loading && !cfg) {
    return (
      <div className={spStyles.centered}>
        <Spin />
      </div>
    );
  }
  if (!cfg) return null;

  const isCalendly = connection?.provider === "calendly";
  const avail = cfg.availability;
  const setupCfg = (patch) => setCfg((prev) => ({ ...prev, ...patch }));
  const update = (patch) => {
    setCfg((prev) => ({ ...prev, ...patch }));
    setDirty(true);
  };
  const toSettings = () => setView("settings");

  if (view === "setup1" || view === "calendar") {
    const setup = view === "setup1";
    // Calendly brings its own hours and event types, so choosing it finishes setup.
    const finishWithCalendly = async (conn) => {
      setConnection(conn);
      if (setup && !(await persist(cfg, true))) return;
      setView(setup ? "appointments" : "settings");
      load();
    };
    return (
      <CalendarScreen
        botId={botId}
        connection={connection}
        providers={meta.providers || {}}
        step={setup ? 1 : null}
        onBack={setup ? null : toSettings}
        onConnect={handleConnect}
        onCalendly={finishWithCalendly}
        onNone={setup ? () => setView("setup2") : null}
        onNext={setup ? () => (isCalendly ? finishWithCalendly(connection) : setView("setup2")) : null}
        onDisconnect={handleDisconnect}
        t={t}
      />
    );
  }

  if (view === "setup2" || view === "hours") {
    const setup = view === "setup2";
    return (
      <HoursScreen
        key={view}
        weekly={avail.weekly_hours}
        step={setup ? 2 : null}
        saving={saving}
        onBack={setup ? () => setView("setup1") : toSettings}
        onDone={(weekly_hours) => {
          const next = { ...cfg, availability: { ...avail, weekly_hours } };
          if (setup) {
            setupCfg(next);
            setView("setup3");
          } else saveAndGo(next, "settings");
        }}
        t={t}
      />
    );
  }

  if (view === "setup3" || view === "length") {
    const setup = view === "setup3";
    return (
      <LengthScreen
        key={view}
        minutes={cfg.services[0]?.duration_min}
        step={setup ? 3 : null}
        saving={saving}
        onBack={setup ? () => setView("setup2") : toSettings}
        onDone={(minutes) => {
          // One appointment length for now: it is also the step, so the open hours split into times this long.
          const first = cfg.services[0];
          const named = first.id === "default" && first.name === "Appointment" ? { ...first, name: t("sonic_calendar.default_service") } : first;
          const services = setup ? [{ ...named, duration_min: minutes }] : cfg.services.map((x, i) => (i === 0 ? { ...x, duration_min: minutes } : x));
          const next = { ...cfg, services, availability: { ...avail, slot_step_min: minutes } };
          if (setup) {
            setupCfg(next);
            setView("setup4");
          } else saveAndGo(next, "settings");
        }}
        t={t}
      />
    );
  }

  if (view === "setup4" || view === "link") {
    const setup = view === "setup4";
    return (
      <LinkScreen
        key={view}
        base={meta.linkBase}
        slug={slug || meta.suggestedSlug}
        savedUrl={cfg.booking_page ? meta.pageUrl : null}
        step={setup ? 4 : null}
        saving={saving}
        onBack={setup ? () => setView("setup3") : toSettings}
        onDone={async (name) => {
          // The last setup answer turns booking on, in chat and on the booking link.
          if (!(await persist({ ...cfg, booking_page: true }, setup ? true : enabled, name))) return;
          message.success(t("sonic_calendar.saved"));
          setView(setup ? "appointments" : "settings");
        }}
        t={t}
      />
    );
  }

  if (view === "settings") {
    const dayLabel = (d) => t(`sonic_prospect.handoff_day_${d}`);
    const names = (list) =>
      (list || [])
        .filter((s) => s.active !== false && s.name)
        .map((s) => s.name)
        .join(", ");
    const provider = PROVIDERS.find((p) => p.id === connection?.provider);
    const activeServices = cfg.services.filter((x) => x.active !== false);
    const single = !isCalendly && activeServices.length === 1;
    const rows = [
      {
        key: "hours",
        title: t("sonic_calendar.opening_hours"),
        value: isCalendly ? t("sonic_calendar.set_in_calendly") : hoursSummary(avail.weekly_hours, dayLabel) ?? t("sonic_calendar.hours_mixed"),
        view: isCalendly ? null : "hours",
      },
      {
        key: "length",
        title: t("sonic_calendar.length_row"),
        // Several appointment types (More options) or Calendly event types: listed here, changed there.
        value: single ? t("sonic_calendar.minutes", { count: activeServices[0].duration_min }) : names(isCalendly ? meta.calendlyServices : cfg.services) || "—",
        view: single ? "length" : null,
      },
      {
        key: "daysoff",
        title: t("sonic_calendar.days_off"),
        value: isCalendly ? t("sonic_calendar.set_in_calendly") : daysOffSummary(cfg.overrides || [], t),
        view: isCalendly ? null : "daysoff",
      },
      {
        key: "staff",
        title: t("sonic_calendar.staff_title"),
        value: isCalendly
          ? t("sonic_calendar.set_in_calendly")
          : names((cfg.staff || []).filter((m) => m.active !== false)) || t("sonic_calendar.staff_none_short"),
        view: isCalendly ? null : "staff",
      },
      {
        key: "calendar",
        title: t("sonic_calendar.calendar_row"),
        value: provider
          ? `${t(provider.name)}${connection.status === "connected" ? "" : ` · ${t("sonic_calendar.reconnect_needed")}`}`
          : t("sonic_calendar.not_connected"),
        view: "calendar",
      },
      {
        key: "link",
        title: t("sonic_calendar.link_title"),
        value: cfg.booking_page && meta.pageUrl ? meta.pageUrl.replace(/^https?:\/\//, "") : t("sonic_calendar.off"),
        view: "link",
      },
    ];
    return (
      <SettingsScreen
        enabled={enabled}
        saving={saving}
        onToggle={(v) => persist(cfg, v)}
        rows={rows}
        onOpen={setView}
        onMore={() => setView("more")}
        onBack={() => setView("appointments")}
        t={t}
      />
    );
  }

  if (view === "staff") {
    return (
      <StaffScreen
        key={JSON.stringify(cfg.staff || [])}
        staff={cfg.staff || []}
        services={cfg.services}
        calendarConnected={!!connection && connection.provider !== "calendly"}
        saving={saving}
        onBack={toSettings}
        onDone={(staff) => saveAndGo({ ...cfg, staff }, "settings")}
        onHours={async (staff, id) => {
          if (await persist({ ...cfg, staff })) {
            setHoursFor(id);
            setView("staffhours");
          }
        }}
        t={t}
      />
    );
  }

  if (view === "staffhours") {
    const member = (cfg.staff || []).find((m) => m.id === hoursFor);
    if (!member) return null;
    return (
      <HoursScreen
        key={member.id}
        weekly={member.weekly_hours || avail.weekly_hours}
        title={t("sonic_calendar.staff_hours_title", { name: member.name })}
        backLabel={t("sonic_calendar.staff_title")}
        saving={saving}
        onBack={() => setView("staff")}
        onDone={(weekly_hours) => saveAndGo({ ...cfg, staff: cfg.staff.map((m) => (m.id === member.id ? { ...m, weekly_hours } : m)) }, "staff")}
        t={t}
      />
    );
  }

  if (view === "daysoff") {
    return <DaysOffScreen overrides={cfg.overrides || []} saving={saving} onBack={toSettings} onDone={(overrides) => saveAndGo({ ...cfg, overrides }, "settings")} t={t} />;
  }

  if (view === "add") {
    const services = isCalendly ? meta.calendlyServices || [] : cfg.services;
    return (
      <AddAppointmentScreen
        botId={botId}
        services={services}
        staff={isCalendly ? [] : cfg.staff || []}
        tz={avail.timezone}
        locale={i18n.language || "en"}
        onBack={() => setView("appointments")}
        onDone={() => setView("appointments")}
        t={t}
      />
    );
  }

  if (view === "block") {
    return (
      <BlockTimeScreen
        tz={avail.timezone}
        saving={saving}
        onBack={() => setView("appointments")}
        onDone={(block) => {
          // Keep only blocks that haven't ended; the API caps the list too.
          const now = Date.now();
          const blocks = [...(cfg.blocks || []).filter((b) => new Date(b.end).getTime() > now), block];
          saveAndGo({ ...cfg, blocks }, "appointments");
        }}
        t={t}
      />
    );
  }

  if (view === "more") {
    return (
      <MoreOptions
        botId={botId}
        cfg={cfg}
        update={update}
        meta={meta}
        onMeta={(patch) => setMeta((m) => ({ ...m, ...patch }))}
        connection={connection}
        calendars={calendars}
        onPick={handleCalendarPick}
        isCalendly={isCalendly}
        dirty={dirty}
        saving={saving}
        onSave={async () => {
          if (await persist()) message.success(t("sonic_calendar.saved"));
        }}
        onBack={() => {
          // Unsaved edits here are dropped: reload what is stored.
          if (dirty) load();
          toSettings();
        }}
        t={t}
      />
    );
  }

  return (
    <AppointmentsScreen
      botId={botId}
      tz={avail.timezone}
      enabled={enabled}
      blocks={cfg.blocks || []}
      canBlock={!isCalendly}
      limitReached={meta.limitReached}
      onAdd={(kind) => setView(kind === "block" ? "block" : "add")}
      onRemoveBlock={async (id) => {
        if (await persist({ ...cfg, blocks: (cfg.blocks || []).filter((b) => b.id !== id) })) message.success(t("sonic_calendar.unblocked"));
      }}
      // The Billing drawer (monthly limit) belongs to the Sonic Prospect page around this tab.
      onRaiseLimit={() => window.dispatchEvent(new Event("sonic-prospect-open-billing"))}
      onSettings={toSettings}
      staff={cfg.staff || []}
      t={t}
    />
  );
};

export default CalendarTab;
